/**
 * Что проверка версии узнала о состоянии приложения — для тех, кто спросить об этом иначе не
 * может. Один набор флагов на вкладку, как `outdatedState`, и по той же причине: читают их
 * модули и хуки вне дерева компонентов, а `useAppUpdateGate` смонтирован ровно один раз.
 */

/**
 * Показываем ли прямо сейчас баннер обновления или экран блокировки.
 *
 * Нужен нативным фичам, которые тоже хотят заговорить с человеком: карточка оценки Play,
 * например, легла бы поверх предложения обновиться — два системных окна подряд в первые же
 * секунды после действия.
 */
let updatePromptVisible = false;

/**
 * Читатели флага. Снимка мало: чек-лист разрешений монтируется по своему поводу и в свой
 * момент — когда доехало состояние устройства, — и вполне может успеть до того, как проверка
 * версии вернётся с ответом. Прочитав флаг один раз при отрисовке, он лёг бы поверх экрана
 * работ и остался бы там.
 */
const promptListeners = new Set<() => void>();

/**
 * Разрешено ли просить оценку в сторе (`reviewPrompt` в `/app/status.json`).
 *
 * Живёт здесь, а не в `features/store-review`, потому что `/app/status.json` — единственный
 * конфиг, который приложение читает в обход шлюза, и владеет им эта фича. Значение по
 * умолчанию — «разрешено»: файл раздаётся руками, и отсутствие поля не должно выключать то,
 * что уже собрано в APK. Выключается явным `false` — то есть без релиза, за минуту.
 */
let reviewPromptEnabled = true;

export const setUpdatePromptVisible = (value: boolean) => {
  if (updatePromptVisible === value) {
    return;
  }
  updatePromptVisible = value;
  promptListeners.forEach((notify) => notify());
};

export const isUpdatePromptVisible = () => updatePromptVisible;

/** Для `useSyncExternalStore`. Возвращает отписку — её же ждёт эффект React. */
export const subscribeUpdatePrompt = (listener: () => void) => {
  promptListeners.add(listener);
  return () => {
    promptListeners.delete(listener);
  };
};

/**
 * Шлюз ответил «сервер закрыт» — 503 с кодом `server.maintenance`.
 *
 * О работах приложение узнаёт из `/app/status.json`, но спрашивает его редко: на старте и при
 * возвращении из фона, не чаще раза в полчаса. Человек, которого работы застали внутри, всё это
 * время жал бы на кнопки и получал ошибки, ничего не понимая. Шлюз узнаёт раньше и говорит
 * определённее — этот сигнал служит поводом сходить за статусом вне очереди, а решение по-
 * прежнему принимает только сам статус: 503 бывает и от чужой поломки.
 */
let serverClosedListener: (() => void) | null = null;

export const setServerClosedListener = (listener: (() => void) | null) => {
  serverClosedListener = listener;
};

export const reportServerClosed = () => {
  serverClosedListener?.();
};

export const setReviewPromptEnabled = (value: boolean) => {
  reviewPromptEnabled = value;
};

export const isReviewPromptEnabled = () => reviewPromptEnabled;
